import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Grid2,
  InputAdornment,
  TextField
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { listCaptures } from "../../../api/captures";
import { getCaptureById } from "../../../api/captures/capture";
import { getReferenceTableById } from "../../../api/reference";
import { getVariables } from "../../../api/variables";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import {
  FlowTestInterpretationForm as FlowTestInterpretationFormType,
  getFlowTestInterpretationFormSchema,
} from "../../../types/forms/flowTest";
import { FlowTestInterpretation } from "../../../types/model/flowTestInterpretation";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import FormAutocomplete from "../../General/FormAutocomplete";
import { PaginatedAutocomplete } from "../../General/PaginatedAutocomplete";

interface InterpretationFormProps {
  entityId: number;
  captureId?: number;
  interpretationData?: FlowTestInterpretation;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: FlowTestInterpretationFormType) => Promise<void>;
}

export default function InterpretationForm({
  entityId,
  captureId,
  interpretationData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<InterpretationFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getFlowTestInterpretationFormSchema(t);
  const methods = useForm<FlowTestInterpretationFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      measuredCaptureId: interpretationData?.measuredCaptureId ?? captureId ?? undefined,
      interpretationMethodId: interpretationData?.interpretationMethodId ?? undefined,
      variableId: interpretationData?.variableId ?? undefined,
      value: interpretationData?.value ?? undefined,
      flag: interpretationData?.flag ?? undefined,
      notes: interpretationData?.notes || "",
    },
  });

  const fetchCaptures = useCallback(
    (search: string, offset: number, limit: number) =>
      listCaptures(entityId, undefined, search, undefined, limit, offset, true),
    [],
  );

  const { data: interpretationMethods = [] } = useReferenceData(
    "flow_test",
    "interpretation_methods"
  );
  
  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(
          false,
          VARIABLE_CATEGORY_CODES.FLOW_TEST_INTERPRETATION,
        );
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, []);
  
  const variableId = methods.watch("variableId");
  const selectedVariable = variables.find((v: any) => v.id === variableId);
  const [lookupTableInfo, setLookupTableInfo] = useState<{ schemaName: string, tableName: string } | null>(null);

  useEffect(() => {
    if (selectedVariable?.isLookup && selectedVariable.lookupTableId) {
      getReferenceTableById(selectedVariable.lookupTableId)
        .then((data) => setLookupTableInfo({schemaName: data.schemaName, tableName: data.tableName}) )
        .catch(error => {
          handleError(error);
          setLookupTableInfo(null);
        });
    } else {
      setLookupTableInfo(null);
    }
  }, [selectedVariable, methods]);
  
  const { data: lookupOptions = [] } = useReferenceData(
    lookupTableInfo?.schemaName as string,
    lookupTableInfo?.tableName as string
  );

  const fetchOneCapture = useCallback(
    (id: number) => getCaptureById(id),
    [],
  );
  
  const handleFormSubmit = async (values: FlowTestInterpretationFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <PaginatedAutocomplete
              name="measuredCaptureId"
              label={`${t("flow-tests.flow-test-interpretation.measured-capture")} *`}
              fetchFn={fetchCaptures}
              fetchOneFn={fetchOneCapture}
              isEditing={isEditing}
              isSaving={isSaving}
              formStyles={formStyles}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <FormAutocomplete
              name="interpretationMethodId"
              label={`${t(
                "flow-tests.flow-test-interpretation.interpretation-method"
              )} *`}
              options={interpretationMethods}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <FormAutocomplete
              name="variableId"
              label={`${t("flow-tests.flow-test-interpretation.variable")} *`}
              options={variables}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          {isEditing ? (
            <>
              <Grid2 size={2}>
                <TextField
                  fullWidth
                  label={t("flow-tests.flow-test-interpretation.flag")}
                  variant="outlined"
                  disabled={!isEditing || isSaving || isDeleting}
                  sx={{ ...formStyles }}
                  {...methods.register("flag")}
                  error={!!methods.formState.errors.flag}
                  helperText={methods.formState.errors.flag?.message}
                />
              </Grid2>

              <Grid2 size={4}>
                {selectedVariable?.isLookup ? (
                  <FormAutocomplete
                    name="value"
                    label={t("flow-tests.flow-test-interpretation.value")}
                    options={lookupOptions}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    disabled={!isEditing || isSaving || isDeleting || !variableId}
                    formStyles={formStyles}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={`${t("flow-tests.flow-test-interpretation.value")} *`}
                    variant="outlined"
                    disabled={!isEditing || isSaving || isDeleting || !variableId}
                    sx={{ ...formStyles }}
                    {...methods.register("value", { valueAsNumber: true })}
                    error={!!methods.formState.errors.value}
                    helperText={methods.formState.errors.value?.message}
                  />
                )}
              </Grid2>
            </>
          ) : (
            <Grid2 size={6}>
              <TextField
                fullWidth
                label={`${t("flow-tests.flow-test-interpretation.value")} *`}
                variant="standard"
                disabled
                sx={{ ...formStyles }}
                {...methods.register("value", { valueAsNumber: true })}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">{methods.watch("flag")}</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{selectedVariable?.unit}</InputAdornment>,
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid2>
          )}

          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              label={t("flow-tests.flow-test-interpretation.notes")}
              placeholder={t(
                "flow-tests.flow-test-interpretation.notes-placeholder"
              )}
              multiline
              minRows={6}
              sx={{ ...formStyles }}
              {...methods.register("notes")}
              error={!!methods.formState.errors.notes}
              helperText={methods.formState.errors.notes?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
